import { formatDistanceToNow } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  deleteConnector,
  getConnectors,
  pauseConnector,
  resumeConnector,
  triggerSync,
} from '../../api/connectors'
import type { ConnectorResponse } from '../../types/api'

interface ConnectorListProps {
  projectId: number
  onAddConnector: () => void
  onOpenDetail: (connector: ConnectorResponse) => void
}

const PROVIDER_LABELS: Record<string, string> = {
  github: 'GitHub',
  google_drive: 'Google Drive',
  fixture: 'Test Fixture',
}

const PROVIDER_ICONS: Record<string, string> = {
  github:
    'M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z',
  google_drive:
    'M12 0L1.608 6v12L12 24l10.392-6V6L12 0zm-1.2 18L4.8 12l2.4-4.2L12 12l-1.2 6zm6.6-6L15 16.2 12 12l5.4-4.2z',
  fixture: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  paused: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  syncing: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  degraded: 'bg-red-500/15 text-red-400 border-red-500/30',
  auth_required: 'bg-red-500/15 text-red-400 border-red-500/30',
  draft: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  disabled: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
}

export function ConnectorList({ projectId, onAddConnector, onOpenDetail }: ConnectorListProps) {
  const [connectors, setConnectors] = useState<ConnectorResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<number | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<ConnectorResponse | null>(null)

  const loadConnectors = useCallback(async () => {
    try {
      const data = await getConnectors(projectId)
      setConnectors(data)
    } catch {
      toast.error('Failed to load connectors')
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    loadConnectors()
  }, [loadConnectors])

  // Poll for status changes when a connector is syncing
  useEffect(() => {
    const hasSyncing = connectors.some((c) => c.status === 'syncing')
    if (!hasSyncing) return

    const interval = setInterval(loadConnectors, 3000)
    return () => clearInterval(interval)
  }, [connectors, loadConnectors])

  async function handleSync(connectorId: number) {
    setActionLoading(connectorId)
    try {
      await triggerSync(connectorId)
      toast.success('Sync triggered')
      await loadConnectors()
    } catch (err) {
      const status = err && typeof err === 'object' && 'status' in err ? Number(err.status) : 0
      if (status === 409) {
        toast.error('A sync is already in progress')
      } else {
        toast.error('Failed to trigger sync')
      }
      await loadConnectors()
    } finally {
      setActionLoading(null)
    }
  }

  async function handlePause(connectorId: number) {
    setActionLoading(connectorId)
    try {
      await pauseConnector(connectorId)
      toast.success('Connector paused')
      await loadConnectors()
    } catch {
      toast.error('Failed to pause connector')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleResume(connectorId: number) {
    setActionLoading(connectorId)
    try {
      await resumeConnector(connectorId)
      toast.success('Connector resumed')
      await loadConnectors()
    } catch {
      toast.error('Failed to resume connector')
    } finally {
      setActionLoading(null)
    }
  }

  async function handleDelete() {
    if (!deleteConfirm) return
    setActionLoading(deleteConfirm.id)
    try {
      await deleteConnector(deleteConfirm.id, 'retain')
      toast.success('Connector removed')
      setDeleteConfirm(null)
      await loadConnectors()
    } catch {
      toast.error('Failed to remove connector')
    } finally {
      setActionLoading(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-harbor-text">Connectors</h3>
          <p className="text-sm text-harbor-text-secondary mt-0.5">
            Sync documents from external sources into this project
          </p>
        </div>
        <button
          onClick={onAddConnector}
          className="px-4 py-2 bg-emerald-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
        >
          Add Connector
        </button>
      </div>

      {connectors.length === 0 ? (
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-8 text-center">
          <div className="text-harbor-text-muted mb-2">
            <svg
              className="w-10 h-10 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          </div>
          <p className="text-harbor-text-secondary text-sm">No connectors configured yet.</p>
          <p className="text-harbor-text-muted text-xs mt-1">
            Connect a GitHub repository to automatically sync documents.
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {connectors.map((connector) => (
            <div
              key={connector.id}
              className="bg-harbor-surface border border-harbor-border rounded-xl p-4 hover:border-harbor-border/80 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  {/* Provider icon */}
                  <div className="flex-shrink-0 w-10 h-10 bg-harbor-surface-raised rounded-lg flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-harbor-text"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d={PROVIDER_ICONS[connector.provider] ?? PROVIDER_ICONS.fixture} />
                    </svg>
                  </div>

                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onOpenDetail(connector)}
                        className="text-harbor-text font-medium text-sm hover:text-emerald-400 transition-colors truncate"
                      >
                        {connector.displayName}
                      </button>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full border ${STATUS_STYLES[connector.status] ?? STATUS_STYLES.draft}`}
                      >
                        {connector.status === 'syncing' && (
                          <span className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse mr-1.5" />
                        )}
                        {connector.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-xs text-harbor-text-muted mt-0.5">
                      {PROVIDER_LABELS[connector.provider] ?? connector.provider}
                      {connector.configJson &&
                        (() => {
                          try {
                            const cfg = JSON.parse(connector.configJson)
                            if (cfg.owner && cfg.repo) return ` \u00b7 ${cfg.owner}/${cfg.repo}`
                          } catch {
                            /* ignore */
                          }
                          return ''
                        })()}
                      {` \u00b7 Updated ${formatDistanceToNow(new Date(connector.updatedAt), { addSuffix: true })}`}
                    </p>
                    {connector.providerError && (
                      <p className="text-xs text-red-400 mt-1 truncate">
                        {connector.providerError}
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                  {connector.status === 'syncing' ? (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-400">
                      <span className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                      Syncing
                    </div>
                  ) : (
                    <button
                      onClick={() => handleSync(connector.id)}
                      disabled={actionLoading === connector.id}
                      className="px-3 py-1.5 text-xs text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors disabled:opacity-50"
                      title="Sync now"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </button>
                  )}

                  {connector.status === 'paused' ? (
                    <button
                      onClick={() => handleResume(connector.id)}
                      disabled={actionLoading === connector.id}
                      className="px-3 py-1.5 text-xs text-harbor-text-secondary hover:text-emerald-400 hover:bg-harbor-surface-raised rounded-lg transition-colors disabled:opacity-50"
                      title="Resume"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                        />
                      </svg>
                    </button>
                  ) : connector.status !== 'draft' ? (
                    <button
                      onClick={() => handlePause(connector.id)}
                      disabled={actionLoading === connector.id}
                      className="px-3 py-1.5 text-xs text-harbor-text-secondary hover:text-amber-400 hover:bg-harbor-surface-raised rounded-lg transition-colors disabled:opacity-50"
                      title="Pause"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 9v6m4-6v6"
                        />
                      </svg>
                    </button>
                  ) : null}

                  <button
                    onClick={() => setDeleteConfirm(connector)}
                    className="px-3 py-1.5 text-xs text-harbor-text-secondary hover:text-red-400 hover:bg-harbor-surface-raised rounded-lg transition-colors"
                    title="Remove"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
          />
          <div className="relative bg-harbor-surface border border-harbor-border rounded-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-harbor-text mb-2">Remove Connector</h3>
            <p className="text-sm text-harbor-text-secondary mb-4">
              Remove &ldquo;{deleteConfirm.displayName}&rdquo;? Synced documents will be kept in the
              project.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-harbor-text-secondary hover:text-harbor-text hover:bg-harbor-surface-raised rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading === deleteConfirm.id}
                className="px-4 py-2 bg-red-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {actionLoading === deleteConfirm.id ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
